import { useMemo } from 'react'
import * as d3 from 'd3-format'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { updatedPriceConfig } from '@/features/payments/config'
import { FreeTrialCoupon } from '../../mutations'

type UseOrderCalculationProps = {
  packageName: string
  recurring: string
  applyCoupon?: FreeTrialCoupon
}

export const useOrderCalculation = ({
  packageName,
  recurring,
  applyCoupon,
}: UseOrderCalculationProps) => {
  const { i18n } = useTranslation()

  const priceObj = useMemo(() => {
    const config = updatedPriceConfig[packageName || 'small']
    const initialPrice = config?.price?.[recurring || 'year'] || 0

    let discount = 0
    let finalPrice = initialPrice

    if (applyCoupon) {
      switch (applyCoupon.discount_type) {
        case 'fixed':
          discount = applyCoupon.discount_amount
          finalPrice = Math.max(initialPrice - discount, 0)
          break
        case 'percentage':
          discount = (Math.min(applyCoupon.discount_amount, 100) * initialPrice) / 100
          finalPrice = Math.max(initialPrice - discount, 0)
          break
      }
    }

    const VAT = 1.07
    const FORMAT = ',.2f'

    const preVat = finalPrice / VAT
    const vat = finalPrice - preVat

    return {
      initial: d3.format(FORMAT)(initialPrice),
      preVat: d3.format(FORMAT)(preVat),
      vat: d3.format(FORMAT)(vat),
      discount: d3.format(FORMAT)(discount),
      finalPrice: d3.format(FORMAT)(finalPrice),
    }
  }, [recurring, packageName, applyCoupon])

  const dueDate = useMemo(() => {
    const target = dayjs().add(packageName === 'large' ? 7 : 14, 'day')

    const formattedDate = target.locale(i18n.language).format('DD/MM/YYYY')

    return {
      date: target.get('date'),
      month: target.get('month') + 1,
      year: target.get('year'),
      duration: packageName === 'large' ? 7 : 14,
      formatted: formattedDate,
    }
  }, [packageName, i18n.language])

  return { priceObj, dueDate }
}
